import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch, Select, DatePicker, Tag
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, ExperimentOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled, DownloadOutlined,
    FilterOutlined, ClearOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManagePlants = () => {
    const [plants, setPlants] = useState([]);
    const [lands, setLands] = useState([]);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [plantCategories, setPlantCategories] = useState([]);

    // Advanced filter state (cascade: All Plants -> Land -> Section -> Field, + Category)
    const [filterLand, setFilterLand] = useState(0);
    const [filterSection, setFilterSection] = useState(0);
    const [filterField, setFilterField] = useState(0);
    const [filterPcId, setFilterPcId] = useState(0);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterSections, setFilterSections] = useState([]);
    const [filterFields, setFilterFields] = useState([]);

    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchData = async () => {
        try {
            const [landsRes, catsRes] = await Promise.all([
                api.get('/masterdropdown/listof_activelands.php'),
                api.get('/masterdropdown/listof_activeplantcategories.php')
            ]);
            setLands(landsRes.data.dataset);
            setPlantCategories(catsRes.data.dataset);
        } catch {
            message.error("Failed to load reference data.");
        }
    };

    const fetchSectionsByLand = async (landId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activesections.php?land_id=${landId}`);
            setSections(res.data.dataset);
        } catch {
            message.error("Failed to load sections.");
        }
    };

    const fetchFieldsBySection = async (sectionId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activefields.php?section_id=${sectionId}`);
            setFields(res.data.dataset);
        } catch {
            message.error("Failed to load fields.");
        }
    };

    // ---- Advanced filter cascade handlers ----
    const fetchFilterSections = async (landId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activesections.php?land_id=${landId}`);
            setFilterSections(res.data.dataset);
        } catch {
            message.error("Failed to load sections.");
        }
    };

    const fetchFilterFields = async (sectionId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activefields.php?section_id=${sectionId}`);
            setFilterFields(res.data.dataset);
        } catch {
            message.error("Failed to load fields.");
        }
    };

    const handleFilterLandChange = (val) => {
        const v = val || 0;
        setFilterLand(v);
        setFilterSection(0);
        setFilterField(0);
        setFilterSections([]);
        setFilterFields([]);
        if (v > 0) fetchFilterSections(v);
    };

    const handleFilterSectionChange = (val) => {
        const v = val || 0;
        setFilterSection(v);
        setFilterField(0);
        setFilterFields([]);
        if (v > 0) fetchFilterFields(v);
    };

    const handleFilterFieldChange = (val) => {
        setFilterField(val || 0);
    };

    const handleFilterPcChange = (val) => {
        setFilterPcId(val || 0);
    };

    const handleFilterStatusChange = (val) => {
        setFilterStatus(val || '');
    };

    const handleResetFilter = () => {
        setFilterLand(0);
        setFilterSection(0);
        setFilterField(0);
        setFilterPcId(0);
        setFilterStatus('');
        setFilterSections([]);
        setFilterFields([]);
    };

    const fetchPlants = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const params = { page, limit: pageSize };
            if (filterLand > 0) params.land_id = filterLand;
            if (filterSection > 0) params.section_id = filterSection;
            if (filterField > 0) params.field_id = filterField;
            if (filterPcId > 0) params.pc_id = filterPcId;
            if (filterStatus) params.status = filterStatus;
            const res = await api.get('/farmSetUp/plant/list.php', { params });
            setPlants(res.data.dataset.map(item => ({
                plantId: item.plant_id,
                fieldId: item.field_id,
                sectionId: item.section_id,
                landId: item.land_id,
                landName: item.land_name,
                sectionName: item.section_name,
                fieldCode: item.field_code,
                plantCode: item.plant_code,
                pcId: item.pc_id,
                pcName: item.pc_name,
                plantingDate: item.planting_date,
                currentStatus: item.current_status,
                plantActive: item.plant_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch plants.");
        } finally {
            setLoading(false);
        }
    }, [filterLand, filterSection, filterField, filterPcId, filterStatus]);

    // Refetch whenever the cascade filters change (fetchPlants is recreated
    // because its deps include the filter state). Reference data loads once.
    useEffect(() => {
        fetchPlants(1, pagination.pageSize || 10);
    }, [fetchPlants]);

    useEffect(() => {
        fetchData();
    }, []);

    const handleTableChange = (pg) => fetchPlants(pg.current, pg.pageSize);

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/plant/add.php', {
                land_id: values.landId,
                section_id: values.sectionId,
                field_id: values.fieldId,
                plant_code: values.plantCode,
                pc_id: values.pcId,
                planting_date: values.plantingDate.format('YYYY-MM-DD'),
                current_status: values.currentStatus,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Plant added successfully.");
                setIsAddModalVisible(false);
                fetchPlants(pagination.current, pagination.pageSize);
                addForm.resetFields();
                setSections([]);
                setFields([]);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add plant.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        fetchSectionsByLand(record.landId);
        fetchFieldsBySection(record.sectionId);
        editForm.setFieldsValue({
            landId: record.landId,
            sectionId: record.sectionId,
            fieldId: record.fieldId,
            plantCode: record.plantCode,
            pcId: record.pcId,
            plantingDate: dayjs(record.plantingDate),
            currentStatus: record.currentStatus,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/plant/edit.php', {
                plant_id: currentItem.plantId,
                land_id: values.landId,
                section_id: values.sectionId,
                field_id: values.fieldId,
                plant_code: values.plantCode,
                pc_id: values.pcId,
                planting_date: values.plantingDate.format('YYYY-MM-DD'),
                current_status: values.currentStatus,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Plant updated successfully.");
                setIsModalVisible(false);
                fetchPlants(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update plant.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/farmSetUp/plant/toggle.php', {
                plant_id: record.plantId,
                plant_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchPlants(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const getStatusTag = (status) => {
        const colors = {
            growing: 'green',
            harvested: 'blue',
            dead: 'red',
            dormant: 'orange'
        };
        return <Tag color={colors[status] || 'default'}>{status.toUpperCase()}</Tag>;
    };

    const handleExport = async () => {
        try {
            // Respect the active advanced filter (land -> section -> field)
            const res = await api.get('/reports/export_plants.php', {
                responseType: 'blob',
                params: {
                    ...(filterLand > 0 && { land_id: filterLand }),
                    ...(filterSection > 0 && { section_id: filterSection }),
                    ...(filterField > 0 && { field_id: filterField }),
                    ...(filterPcId > 0 && { pc_id: filterPcId }),
                    ...(filterStatus && { status: filterStatus }),
                }
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `plants_report_${dayjs().format('YYYYMMDD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to download export.");
        }
    };

    const columns = [
        {
            title: 'Plant Code', dataIndex: 'plantCode', key: 'plantCode',
            ...getColumnSearchProps('plantCode'),
        },
        {
            title: 'Category', dataIndex: 'pcName', key: 'pcName',
            ...getColumnSearchProps('pcName'),
        },
        {
            title: 'Location', key: 'location',
            render: (_, r) => <Typography.Text size="small">{r.landName} {">"} {r.sectionName} {">"} {r.fieldCode}</Typography.Text>
        },
        {
            title: 'Planting Date', dataIndex: 'plantingDate', key: 'plantingDate',
            render: (d) => dayjs(d).format('YYYY-MM-DD')
        },
        {
            title: 'Status', dataIndex: 'currentStatus', key: 'currentStatus',
            render: (s) => getStatusTag(s)
        },
        {
            title: 'Active', dataIndex: 'plantActive', key: 'plantActive',
            align: 'center', width: 110,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Plant">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Switch
                        size="small"
                        checked={record.plantActive == 1}
                        onChange={(checked) => handleToggle(record, checked)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { href: '/farmoverview', title: 'Farm Overview' },
                                { title: 'Manage Plants' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ExperimentOutlined /> Manage Plants
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Track individual plants across farm fields and categories</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Plant Inventory</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Register New Plant
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleExport}
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </div>

                    {/* Advanced Filter: All Plants -> Land -> Section -> Field */}
                    <div style={{ marginBottom: 16 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Land / All Plants:</div>
                                <Select allowClear
                                    style={{ width: "100%" }}
                                    value={filterLand || 0}
                                    onChange={handleFilterLandChange}
                                    placeholder="All Plants"
                                >
                                    <Option value={0}>All Plants</Option>
                                    {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Section:</div>
                                <Select allowClear
                                    style={{ width: "100%" }}
                                    value={filterSection || 0}
                                    onChange={handleFilterSectionChange}
                                    disabled={!filterLand}
                                    placeholder={filterLand ? "Select Section" : "Select a land first"}
                                >
                                    {filterSections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Field:</div>
                                <Select allowClear
                                    style={{ width: "100%" }}
                                    value={filterField || 0}
                                    onChange={handleFilterFieldChange}
                                    disabled={!filterSection}
                                    placeholder={filterSection ? "Select Field" : "Select a section first"}
                                >
                                    {filterFields.map(f => <Option key={f.value} value={f.value}>{f.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Plant Category:</div>
                                <Select allowClear
                                    style={{ width: "100%" }}
                                    value={filterPcId || 0}
                                    onChange={handleFilterPcChange}
                                    placeholder="All Categories"
                                >
                                    <Option value={0}>All Categories</Option>
                                    {plantCategories.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select allowClear
                                    style={{ width: "100%" }}
                                    value={filterStatus}
                                    onChange={handleFilterStatusChange}
                                    placeholder="All Statuses"
                                >
                                    <Option value="">All Statuses</Option>
                                    <Option value="growing">Growing</Option>
                                    <Option value="harvested">Harvested</Option>
                                    <Option value="dead">Dead</Option>
                                    <Option value="dormant">Dormant</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} lg={4}>
                                <Row gutter={8} className="fluid-filter-actions">
                                    <Col span={12}>
                                        <Button
                                            type="primary"
                                            icon={<FilterOutlined />}
                                            onClick={() => fetchPlants(1, pagination.pageSize)}
                                            className="fluid-btn-primary"
                                            block
                                        >
                                            Apply
                                        </Button>
                                    </Col>
                                    <Col span={12}>
                                        <Button
                                            icon={<ClearOutlined />}
                                            onClick={handleResetFilter}
                                            className="fluid-btn-secondary"
                                            block
                                        >
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={plants}
                            rowKey="plantId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 1000 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Register New Plant"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); setSections([]); setFields([]); }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={6}>
                            <Form.Item label="Land" name="landId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Land" onChange={(val) => { addForm.setFieldsValue({ sectionId: undefined, fieldId: undefined }); fetchSectionsByLand(val); }}>
                                    {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Section" name="sectionId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Section" disabled={!sections.length} onChange={(val) => { addForm.setFieldsValue({ fieldId: undefined }); fetchFieldsBySection(val); }}>
                                    {sections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Field" name="fieldId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Field" disabled={!fields.length}>
                                    {fields.map(f => <Option key={f.value} value={f.value}>{f.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Plant Code" name="plantCode" rules={[{ required: true }]}>
                                <Input placeholder="PL-001" maxLength={50} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Plant Category" name="pcId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Category">
                                    {plantCategories.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Planting Date" name="plantingDate" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Current Status" name="currentStatus" initialValue="growing">
                                <Select allowClear>
                                    <Option value="growing">Growing</Option>
                                    <Option value="harvested">Harvested</Option>
                                    <Option value="dead">Dead</Option>
                                    <Option value="dormant">Dormant</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); setSections([]); setFields([]); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Register Plant</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Plant Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={800}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col span={8}>
                            <Form.Item label="Land" name="landId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Land" onChange={(val) => { editForm.setFieldsValue({ sectionId: undefined, fieldId: undefined }); fetchSectionsByLand(val); }}>
                                    {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Section" name="sectionId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Section" onChange={(val) => { editForm.setFieldsValue({ fieldId: undefined }); fetchFieldsBySection(val); }}>
                                    {sections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Field" name="fieldId" rules={[{ required: true }]}>
                                <Select allowClear placeholder="Select Field">
                                    {fields.map(f => <Option key={f.value} value={f.value}>{f.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Form.Item label="Plant Code" name="plantCode" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Plant Category" name="pcId" rules={[{ required: true }]}>
                                <Select allowClear>
                                    {plantCategories.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Form.Item label="Planting Date" name="plantingDate" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Current Status" name="currentStatus">
                                <Select allowClear>
                                    <Option value="growing">Growing</Option>
                                    <Option value="harvested">Harvested</Option>
                                    <Option value="dead">Dead</Option>
                                    <Option value="dormant">Dormant</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManagePlants;
